/*                                                     */
/*                                       */
/*                                                         */
import { DEV } from 'esm-env';
import {
	ERROR_VALUE,
	DERIVED,
	DIRTY,
	EFFECT_PRESERVED,
	STALE_REACTION,
	ASYNC,
	WAS_MARKED,
	DESTROYED,
	CLEAN,
	REACTION_RAN
} from '#client/constants';
import {
	active_reaction,
	active_effect,
	update_reaction,
	increment_write_version,
	set_active_effect,
	push_reaction_value,
	is_destroying_effect,
	update_effect,
	remove_reactions
} from '../runtime.js';
import { equals, safe_equals } from './equality.js';
import * as e from '../errors.js';
import * as w from '../warnings.js';
import {
	async_effect,
	destroy_effect,
	destroy_effect_children,
	effect_tracking,
	teardown
} from './effects.js';
import { eager_effects, internal_set, set_eager_effects, source } from './sources.js';
import { get_error } from '../../shared/dev.js';
import { async_mode_flag, tracing_mode_flag } from '../../flags/index.js';
import { component_context } from '../context.js';
import { UNINITIALIZED } from '../../../constants.js';
import { batch_values, current_batch } from './batch.js';
import { increment_pending, unset_context } from './async.js';
import { deferred, includes, noop } from '../../shared/utils.js';
import { set_signal_status, update_derived_status } from './status.js';

/**
 *
 *
 *
 */
export let reactivity_loss_tracker = null;

/*                                                        */
export function set_reactivity_loss_tracker(v) {
	reactivity_loss_tracker = v;
}

export const recent_async_deriveds = new Set();

/**
 *
 *
 *
 */
/*                    */
export function derived(fn) {
	var flags = DERIVED | DIRTY;
	var parent_derived =
		active_reaction !== null && (active_reaction.f & DERIVED) !== 0
			? /*                  */ (active_reaction)
			: null;

	if (active_effect !== null) {
		/*                                                                       */
		/*                                                                     */
		active_effect.f |= EFFECT_PRESERVED;
	}

	/*                     */
	const signal = {
		ctx: component_context,
		deps: null,
		effects: null,
		equals,
		f: flags,
		fn,
		reactions: null,
		rv: 0,
		v: /*            */ (UNINITIALIZED),
		wv: 0,
		parent: parent_derived ?? active_effect,
		ac: null
	};

	if (DEV && tracing_mode_flag) {
		signal.created = get_error('created at');
	}

	return signal;
}

/**
 *
 *
 *
 *
 *
 */
/*                    */
export function async_derived(fn, label, location) {
	let parent = /*                        */ (active_effect);

	if (parent === null) {
		e.async_derived_orphan();
	}

	var promise = /*                     */ (/*                  */ (undefined));
	var signal = source(/*            */ (UNINITIALIZED));

	if (DEV) signal.label = label;

	/*                                                       */
	var should_suspend = !active_reaction;

	/*                                                     */
	var deferreds = new Map();

	async_effect(() => {
		if (DEV) {
			reactivity_loss_tracker = {
				effect: /*                 */ (active_effect),
				warned: false
			};
		}

		var effect = /*                 */ (active_effect);

		/*                                         */
		var d = deferred();
		promise = d.promise;

		try {
			/*                                                                                 */
			/*                                                                                  */
			/*                                                                         */
			Promise.resolve(fn()).then(d.resolve, d.reject).finally(unset_context);
		} catch (error) {
			d.reject(error);
			unset_context();
		}

		if (DEV) {
			reactivity_loss_tracker = null;
		}

		var batch = /*                */ (current_batch);

		if (should_suspend) {
			/*                                                                               */
			/*                                                                             */
			/*                                                                         */
			if ((effect.f & REACTION_RAN) !== 0) {
				var decrement_pending = increment_pending();
			}

			if (/*                   */ (parent.b).is_rendered()) {
				deferreds.get(batch)?.reject(STALE_REACTION);
				deferreds.delete(batch); /*                                                    */
			} else {
				/*                                                                                         */
				/*                                                                                  */
				for (const d of deferreds.values()) {
					d.reject(STALE_REACTION);
				}
				deferreds.clear();
			}

			deferreds.set(batch, d);
		}

		/**
 *
 *
 */
		const handler = (value, error = undefined) => {
			if (DEV) {
				reactivity_loss_tracker = null;
			}

			if (decrement_pending) {
				/*                                                 */
				/*                                                 */
				var skip = error === STALE_REACTION;
				decrement_pending(skip);
			}

			if (error === STALE_REACTION || (effect.f & DESTROYED) !== 0) {
				return;
			}

			batch.activate();

			if (error) {
				signal.f |= ERROR_VALUE;

				/*                                                              */
				internal_set(signal, error);
			} else {
				if ((signal.f & ERROR_VALUE) !== 0) {
					signal.f ^= ERROR_VALUE;
				}

				internal_set(signal, value);

				/*                                         */
				for (const [b, d] of deferreds) {
					deferreds.delete(b);
					if (b === batch) break;
					d.reject(STALE_REACTION);
				}

				if (DEV && location !== undefined) {
					recent_async_deriveds.add(signal);

					setTimeout(() => {
						if (recent_async_deriveds.has(signal)) {
							w.await_waterfall(/*                 */ (signal.label), location);
							recent_async_deriveds.delete(signal);
						}
					});
				}
			}

			batch.deactivate();
		};

		d.promise.then(handler, (e) => handler(null, e || 'unknown'));
	});

	teardown(() => {
		for (const d of deferreds.values()) {
			d.reject(STALE_REACTION);
		}
	});

	if (DEV) {
		/*                                                */
		/*                            */
		signal.f |= ASYNC;
	}

	return new Promise((fulfil) => {
		/*                        */
		function next(p) {
			function go() {
				if (p === promise) {
					fulfil(signal);
				} else {
					/*                                               */
					/*                                               */
					next(promise);
				}
			}

			p.then(go, go);
		}

		next(promise);
	});
}

/**
 *
 *
 *
 */
/*                    */
export function user_derived(fn) {
	const d = derived(fn);

	if (!async_mode_flag) push_reaction_value(d);

	return d;
}

/**
 *
 *
 *
 */
/*                    */
export function derived_safe_equal(fn) {
	const signal = derived(fn);
	signal.equals = safe_equals;
	return signal;
}

/**
 *
 *
 */
export function destroy_derived_effects(derived) {
	var effects = derived.effects;

	if (effects !== null) {
		derived.effects = null;

		for (var i = 0; i < effects.length; i += 1) {
			destroy_effect(/*                 */ (effects[i]));
		}
	}
}

/**
 *
 *
 *
 */
let stack = [];

/**
 *
 *
 */
function get_derived_parent_effect(derived) {
	var parent = derived.parent;
	while (parent !== null) {
		if ((parent.f & DERIVED) === 0) {
			/*                                                                 */
			/*                                                                      */
			return (parent.f & DESTROYED) === 0 ? /*                 */ (parent) : null;
		}
		parent = parent.parent;
	}
	return null;
}

/**
 *
 *
 *
 */
export function execute_derived(derived) {
	var value;
	var prev_active_effect = active_effect;

	set_active_effect(get_derived_parent_effect(derived));

	if (DEV) {
		let prev_eager_effects = eager_effects;
		set_eager_effects(new Set());
		try {
			if (includes.call(stack, derived)) {
				e.derived_references_self();
			}

			stack.push(derived);

			derived.f &= ~WAS_MARKED;
			destroy_derived_effects(derived);
			value = update_reaction(derived);
		} finally {
			set_active_effect(prev_active_effect);
			set_eager_effects(prev_eager_effects);
			stack.pop();
		}
	} else {
		try {
			derived.f &= ~WAS_MARKED;
			destroy_derived_effects(derived);
			value = update_reaction(derived);
		} finally {
			set_active_effect(prev_active_effect);
		}
	}

	return value;
}

/**
 *
 *
 */
export function update_derived(derived) {
	var old_value = derived.v;
	var value = execute_derived(derived);

	if (!derived.equals(value)) {
		derived.wv = increment_write_version();

		/*                                                                    */
		/*                                                               */
		/*                                                              */
		/*                                                     */
		if (!current_batch?.is_fork || derived.deps === null) {
			derived.v = value;
			current_batch?.capture(derived, old_value);

			/*                                                       */
			if (derived.deps === null) {
				set_signal_status(derived, CLEAN);
				return;
			}
		}
	}

	/*                                                    */
	/*                                               */
	if (is_destroying_effect) {
		return;
	}

	/*                                                              */
	/*                                                        */
	if (batch_values !== null) {
		/*                                                                      */
		/*                                                                */
		if (effect_tracking() || current_batch?.is_fork) {
			batch_values.set(derived, value);
		}
	} else {
		update_derived_status(derived);
	}
}

/**
 *
 */
export function freeze_derived_effects(derived) {
	if (derived.effects === null) return;

	for (const e of derived.effects) {
		/*                                                             */
		if (e.teardown || e.ac) {
			e.teardown?.();
			e.ac?.abort(STALE_REACTION);

			/*                                                           */
			/*                                                            */
			/*                                                    */
			/*                                  */
			e.teardown = noop;
			e.ac = null;

			remove_reactions(e, 0);
			destroy_effect_children(e);
		}
	}
}

/**
 *
 */
export function unfreeze_derived_effects(derived) {
	if (derived.effects === null) return;

	for (const e of derived.effects) {
		/*                                                              */
		/*                                   */
		if (e.teardown) {
			update_effect(e);
		}
	}
}
